import { People, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { withForm } from "@/components/Forms";
import { useAllOrganizations } from "@/hooks/use-all-organizations";
import { deliveryOptionFormOpts } from "../option.form.opts";
import { PartnerRowList } from "./partners-list";

export const PartnerList = withForm({
  ...deliveryOptionFormOpts,
  render: ({ form }) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");
    const { organizations: orgs, isLoading, error } = useAllOrganizations();
    if (isLoading) {
      return (
        <Box sx={{ my: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t("deliveryOption.visibility.loading")}
          </Typography>
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ my: 2, display: "flex", justifyContent: "center", gap: 1 }}>
          <Typography variant="body2" color="error">
            {t("deliveryOption.visibility.error")}
          </Typography>
        </Box>
      );
    }
    if (!orgs.length) {
      return (
        <Box sx={{ my: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t("deliveryOption.visibility.noData")}
          </Typography>
        </Box>
      );
    }

    return (
      <form.Subscribe selector={(s) => s.values.visibilityMode}>
        {(mode) =>
          mode !== "PARTNER_LIST" ? null : (
            <Box mt={4}>
              <Divider sx={{ mb: 3 }} />

              <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
                <Box
                  sx={{
                    width: 30,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: "warning.50",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "warning.main",
                  }}
                >
                  <People sx={{ fontSize: 18 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
                  {t("deliveryOption.visibility.partnerAccess")}
                </Typography>
                <form.Subscribe
                  selector={(s) => s.values.partnerAssignments.filter((a) => a.selected).length}
                >
                  {(count) => (
                    <Chip
                      label={
                        count > 0
                          ? `${count} ${t("deliveryOption.visibility.selected")}`
                          : t("deliveryOption.visibility.noneSelected")
                      }
                      size="small"
                      variant="filled"
                      sx={{
                        fontWeight: 600,
                        bgcolor: count > 0 ? "primary.main" : "grey.300",
                        color: count > 0 ? "white" : "grey.700",
                      }}
                    />
                  )}
                </form.Subscribe>
              </Stack>

              <Typography variant="body2" color="text.secondary" display="block" mb={2.5} ml={5}>
                {t("deliveryOption.visibility.partnerDescription")}
              </Typography>

              <Stack direction="row" spacing={1.5} mb={2.5} alignItems="center">
                <TextField
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("deliveryOption.visibility.searchPlaceholder")}
                  sx={{ flex: 1 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: "grey.500" }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <form.Subscribe
                  selector={(s) => ({
                    assignments: s.values.partnerAssignments,
                    allSelected:
                      s.values.partnerAssignments.length > 0 &&
                      s.values.partnerAssignments.every((a) => a.selected),
                  })}
                >
                  {({ assignments, allSelected }) => (
                    <Button
                      variant={allSelected ? "outlined" : "contained"}
                      color={allSelected ? "inherit" : "primary"}
                      sx={{ minWidth: 120, fontWeight: 600, textTransform: "none" }}
                      onClick={() =>
                        form.setFieldValue(
                          "partnerAssignments",
                          assignments.map((a) => ({ ...a, selected: !allSelected }))
                        )
                      }
                    >
                      {allSelected
                        ? t("deliveryOption.visibility.deselectAll")
                        : t("deliveryOption.visibility.selectAll")}
                    </Button>
                  )}
                </form.Subscribe>
              </Stack>

              <PartnerRowList form={form} orgs={orgs} search={search} />

              {/* Array-level validation error */}
              <form.AppField name="partnerAssignments">
                {(f) => {
                  const msg = f.state.meta.errors[0]?.message;
                  return msg ? (
                    <Typography variant="caption" color="error" sx={{ mt: 1.5, display: "block" }}>
                      {msg}
                    </Typography>
                  ) : null;
                }}
              </form.AppField>
            </Box>
          )
        }
      </form.Subscribe>
    );
  },
});
